/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Building2, ChevronLeft, ChevronRight, Loader2, Check, Layers, Globe, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import toastService from '../utils/toastService';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

const CLASS_TEXT_MUTED = 'text-base-content/70';
const CLASS_TEXT_PRIMARY = 'text-primary font-bold';

// Walks every page of a paginated list endpoint and concatenates the results,
// since the switcher needs the full set (not just the first page) to search/select from.
async function fetchAllPages(url, params = {}) {
  const limit = 100;
  const first = await apiClient.get(url, { params: { ...params, limit, page: 1 } });
  let all = first.data.data ?? [];
  const { totalPages = 1 } = first.data.pagination ?? {};
  for (let page = 2; page <= totalPages; page += 1) {
    const res = await apiClient.get(url, { params: { ...params, limit, page } });
    all = all.concat(res.data.data ?? []);
  }
  return all;
}

// Case-insensitive A→Z by name, numeric-aware ("Store 2" before "Store 10").
const byNameAsc = (a, b) =>
  (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base', numeric: true });

const AccountSwitcher = ({ onBack, onComplete }) => {
  const HOME_ROUTE = '/';

  const navigate = useNavigate();
  const { user, switchContext, resetContext } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const selectedItemRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const accountRowRefs = useRef({});

  // Platform admin state
  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Account admin state
  const [ownSubAccounts, setOwnSubAccounts] = useState([]);
  const [loadingOwnSubs, setLoadingOwnSubs] = useState(false);

  const isPlatformAdmin = user?.scope === 'platform';
  const isAccountAdmin = user?.scope === 'account';

  // Platform admin: load all accounts and all sub-accounts (grouped by account) on mount
  useEffect(() => {
    if (!isPlatformAdmin) return;
    setLoadingAccounts(true);
    Promise.allSettled([
      fetchAllPages('/accounts'),
      fetchAllPages('/accounts/all/sub-accounts'),
    ])
      .then(([accountsResult, subAccountsResult]) => {
        if (accountsResult.status === 'fulfilled') {
          setAccounts([...accountsResult.value].sort(byNameAsc));
        } else {
          toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
        }
        if (subAccountsResult.status === 'fulfilled') {
          const grouped = {};
          subAccountsResult.value.forEach((sub) => {
            if (!grouped[sub.accountId]) grouped[sub.accountId] = [];
            grouped[sub.accountId].push(sub);
          });
          Object.values(grouped).forEach((list) => list.sort(byNameAsc));
          setSubAccounts(grouped);
        } else {
          toastService.error(`Failed to load ${SUB_ACCOUNT_LABEL.pluralLower}`);
        }
      })
      .finally(() => setLoadingAccounts(false));
  }, [isPlatformAdmin]);

  // Platform admin: auto-expand active account if sub-account is selected
  useEffect(() => {
    if (!isPlatformAdmin || !user?.accountUID || !user?.subAccountUID) return;
    setExpanded((s) => ({ ...s, [user.accountUID]: true }));
  }, [isPlatformAdmin, user?.accountUID, user?.subAccountUID]);

  // Account admin: load their own sub-accounts on mount
  useEffect(() => {
    if (!isAccountAdmin || !user?.accountUID) return;
    if (ownSubAccounts.length > 0) return;
    setLoadingOwnSubs(true);
    fetchAllPages(`/accounts/${user.accountUID}/sub-accounts`)
      .then((data) => setOwnSubAccounts([...data].sort(byNameAsc)))
      .catch(() => toastService.error(`Failed to load ${SUB_ACCOUNT_LABEL.pluralLower}`))
      .finally(() => setLoadingOwnSubs(false));
  }, [isAccountAdmin, user?.accountUID, ownSubAccounts.length]);

  // Auto-scroll to selected scope when loaded
  useEffect(() => {
    if (!selectedItemRef.current) return;
    const timer = setTimeout(() => {
      if (selectedItemRef.current) {
        selectedItemRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [
    user?.subAccountUID,
    user?.accountUID,
    loadingAccounts,
    loadingOwnSubs,
    subAccounts,
    ownSubAccounts,
  ]);

  // Only render for roles that need context switching
  if (!isPlatformAdmin && !isAccountAdmin) return null;

  const toggleExpand = (e, accountId) => {
    e.stopPropagation();
    const next = !expanded[accountId];
    setExpanded((s) => ({ ...s, [accountId]: next }));
    if (next) {
      setTimeout(() => {
        accountRowRefs.current[accountId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }, 120);
    }
  };

  const handleResetContext = async () => {
    if (switching) return;
    setSwitching(true);
    try {
      await resetContext();
      toastService.success(isPlatformAdmin ? `Switched to all ${ACCOUNT_LABEL.pluralLower}` : `Switched to all ${SUB_ACCOUNT_LABEL.pluralLower}`);
      onComplete?.();
      navigate(HOME_ROUTE);
    } catch (err) {
      toastService.error(err?.response?.data?.message ?? 'Failed to reset context');
    } finally {
      setSwitching(false);
    }
  };

  const handleSwitchAccount = async (accountId) => {
    if (switching) return;
    setSwitching(true);
    try {
      await switchContext(accountId);
      toastService.success(`Switched ${ACCOUNT_LABEL.singularLower}`);
      onComplete?.();
      navigate(HOME_ROUTE);
    } catch (err) {
      toastService.error(err?.response?.data?.message ?? `Failed to switch ${ACCOUNT_LABEL.singularLower}`);
    } finally {
      setSwitching(false);
    }
  };

  const handleSwitchSubAccount = async (accountId, subAccountId, e) => {
    if (e) e.stopPropagation();
    if (switching) return;
    setSwitching(true);
    try {
      await switchContext(accountId, subAccountId);
      toastService.success(`Switched to ${SUB_ACCOUNT_LABEL.singularLower}`);
      onComplete?.();
      navigate(HOME_ROUTE);
    } catch (err) {
      toastService.error(err?.response?.data?.message ?? `Failed to switch ${SUB_ACCOUNT_LABEL.singularLower}`);
    } finally {
      setSwitching(false);
    }
  };

  // Filter accounts/subaccounts by query
  const query = searchQuery.trim().toLowerCase();
  const filteredAccounts = isPlatformAdmin
    ? accounts.filter((a) => {
        if (!query) return true;
        const matchAccount = a.name?.toLowerCase().includes(query);
        const matchSub = subAccounts[a.id]?.some((s) => s.name?.toLowerCase().includes(query));
        return matchAccount || matchSub;
      })
    : [];

  const filteredOwnSubAccounts = isAccountAdmin
    ? ownSubAccounts.filter((s) => !query || s.name?.toLowerCase().includes(query))
    : [];

  return (
    <div className="flex flex-col w-full">
      {/* Header with back button */}
      <div className="flex items-center gap-2 p-3 bg-base-100/70 border-b border-base-content/10">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-base-content/10 text-base-content/70 hover:text-base-content transition-colors cursor-pointer"
            aria-label="Back to menu"
          >
            <ChevronLeft size={16} />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-black uppercase tracking-wider text-base-content truncate">
            {isPlatformAdmin ? `Switch ${ACCOUNT_LABEL.singular}` : `Switch ${SUB_ACCOUNT_LABEL.singular}`}
          </p>
        </div>
        {switching && (
          <Loader2 size={15} className="animate-spin text-primary shrink-0" />
        )}
      </div>

      {/* Optional Search bar if multiple items */}
      {((isPlatformAdmin && accounts.length > 1) || (isAccountAdmin && ownSubAccounts.length > 1)) && (
        <div className="px-2.5 pt-2 pb-1 bg-base-200/50">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/40 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isPlatformAdmin ? `Search ${ACCOUNT_LABEL.pluralLower} or ${SUB_ACCOUNT_LABEL.pluralLower}...` : `Search ${SUB_ACCOUNT_LABEL.pluralLower}...`}
              aria-label={isPlatformAdmin ? `Search ${ACCOUNT_LABEL.pluralLower} or ${SUB_ACCOUNT_LABEL.pluralLower}` : `Search ${SUB_ACCOUNT_LABEL.pluralLower}`}
              className="w-full pl-7 pr-2.5 py-1 text-xs rounded-lg bg-base-100 border border-base-content/10 focus:outline-none focus:border-primary/50 text-base-content placeholder:text-base-content/40"
            />
          </div>
        </div>
      )}

      <div ref={scrollContainerRef} className="max-h-72 overflow-y-auto overscroll-contain p-1.5 space-y-0.5">
        {/* ── PLATFORM ADMIN: two-level (accounts → sub-accounts) ── */}
        {isPlatformAdmin && (
          <>
            {/* All Accounts — reset to platform level */}
            {!query && (
              <button
                type="button"
                disabled={switching}
                onClick={handleResetContext}
                className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-xl text-left hover:bg-base-content/5 transition-colors cursor-pointer ${
                  !user?.accountUID ? CLASS_TEXT_PRIMARY + ' bg-primary/10' : CLASS_TEXT_MUTED
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  !user?.accountUID ? 'bg-primary text-primary-content' : 'bg-primary/10 text-primary'
                }`}>
                  <Globe size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black truncate">All {ACCOUNT_LABEL.plural}</p>
                  <p className="text-[10px] text-base-content/40 truncate uppercase tracking-wider">Platform view</p>
                </div>
                {!user?.accountUID && <Check size={14} className="text-primary flex-shrink-0" />}
              </button>
            )}

            {loadingAccounts ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={20} className="animate-spin text-primary" />
              </div>
            ) : filteredAccounts.length === 0 ? (
              <p className="text-base-content/40 text-xs text-center py-6 font-black uppercase">
                {query ? `No matching ${ACCOUNT_LABEL.pluralLower}` : `No ${ACCOUNT_LABEL.pluralLower} found`}
              </p>
            ) : (
              filteredAccounts.map((acct) => {
                const isActiveAccount = acct.id === user?.accountUID;
                const isOpen = expanded[acct.id] || (query && subAccounts[acct.id]?.some(s => s.name?.toLowerCase().includes(query)));
                const subs = (subAccounts[acct.id] ?? []).filter(
                  (s) => !query || s.name?.toLowerCase().includes(query) || acct.name?.toLowerCase().includes(query)
                );

                return (
                  <div
                    key={acct.id}
                    ref={(el) => { accountRowRefs.current[acct.id] = el; }}
                    className="rounded-xl overflow-hidden"
                  >
                    <div
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-base-content/5 transition-colors ${
                        isActiveAccount && !user?.subAccountUID ? CLASS_TEXT_PRIMARY + ' bg-primary/10' : CLASS_TEXT_MUTED
                      }`}
                    >
                      <button
                        type="button"
                        onClick={(e) => toggleExpand(e, acct.id)}
                        className="w-5 h-5 flex items-center justify-center rounded hover:bg-base-content/10 flex-shrink-0 transition-colors cursor-pointer"
                        aria-label={isOpen ? `Collapse ${SUB_ACCOUNT_LABEL.pluralLower}` : `Expand ${SUB_ACCOUNT_LABEL.pluralLower}`}
                      >
                        <ChevronRight size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      <button
                        type="button"
                        ref={(isActiveAccount && !user?.subAccountUID) ? selectedItemRef : null}
                        aria-current={isActiveAccount && !user?.subAccountUID ? 'true' : undefined}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-lg"
                        onClick={() => handleSwitchAccount(acct.id)}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isActiveAccount && !user?.subAccountUID ? 'bg-primary text-primary-content' : 'bg-primary/10 text-primary'
                        }`}>
                          <Building2 size={13} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-black truncate">{acct.name}</p>
                          <p className="text-[10px] text-base-content/40 truncate uppercase tracking-wider">{acct.status || 'Active'}</p>
                        </div>
                        {isActiveAccount && !user?.subAccountUID && (
                          <Check size={14} className="text-primary flex-shrink-0" />
                        )}
                      </button>
                    </div>

                    {isOpen && (
                      <div className="ml-5 pl-2 border-l-2 border-base-content/10 py-1 space-y-0.5">
                        {subs.length === 0 ? (
                          <p className="text-[10px] text-base-content/30 px-3 py-1 uppercase tracking-wider font-bold">No {SUB_ACCOUNT_LABEL.pluralLower}</p>
                        ) : (
                          subs.map((sub) => {
                            const isActiveSub = isActiveAccount && sub.id === user?.subAccountUID;
                            return (
                              <button
                                key={sub.id}
                                ref={isActiveSub ? selectedItemRef : null}
                                type="button"
                                disabled={switching}
                                onClick={(e) => handleSwitchSubAccount(acct.id, sub.id, e)}
                                className={`flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-left hover:bg-base-content/5 transition-colors cursor-pointer ${
                                  isActiveSub ? CLASS_TEXT_PRIMARY + ' bg-primary/10' : 'text-base-content/70'
                                }`}
                              >
                                <Layers size={12} className={`flex-shrink-0 ${isActiveSub ? 'text-primary' : 'opacity-50'}`} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-bold truncate">{sub.name}</p>
                                </div>
                                {isActiveSub && <Check size={13} className="text-primary flex-shrink-0" />}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}

        {/* ── ACCOUNT ADMIN: one-level (sub-accounts only) ── */}
        {isAccountAdmin && (
          <>
            {/* All Sub-accounts — reset to account level */}
            {!query && (
              <button
                type="button"
                disabled={switching}
                onClick={handleResetContext}
                className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-xl text-left hover:bg-base-content/5 transition-colors cursor-pointer ${
                  !user?.subAccountUID ? CLASS_TEXT_PRIMARY + ' bg-primary/10' : CLASS_TEXT_MUTED
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  !user?.subAccountUID ? 'bg-primary text-primary-content' : 'bg-primary/10 text-primary'
                }`}>
                  <Globe size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black truncate">All {SUB_ACCOUNT_LABEL.plural}</p>
                  <p className="text-[10px] text-base-content/40 truncate uppercase tracking-wider">{ACCOUNT_LABEL.singular} view</p>
                </div>
                {!user?.subAccountUID && <Check size={14} className="text-primary flex-shrink-0" />}
              </button>
            )}

            {loadingOwnSubs ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={20} className="animate-spin text-primary" />
              </div>
            ) : filteredOwnSubAccounts.length === 0 ? (
              <p className="text-base-content/40 text-xs text-center py-6 font-black uppercase">
                {query ? `No matching ${SUB_ACCOUNT_LABEL.pluralLower}` : `No ${SUB_ACCOUNT_LABEL.pluralLower} found`}
              </p>
            ) : (
              filteredOwnSubAccounts.map((sub) => {
                const isActive = sub.id === user?.subAccountUID;
                return (
                  <button
                    key={sub.id}
                    ref={isActive ? selectedItemRef : null}
                    type="button"
                    disabled={switching}
                    onClick={() => handleSwitchSubAccount(user.accountUID, sub.id)}
                    className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-xl text-left hover:bg-base-content/5 transition-colors cursor-pointer ${
                      isActive ? CLASS_TEXT_PRIMARY + ' bg-primary/10' : CLASS_TEXT_MUTED
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-primary text-primary-content' : 'bg-primary/10 text-primary'
                    }`}>
                      <Layers size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-black truncate">{sub.name}</p>
                      <p className="text-[10px] text-base-content/40 truncate uppercase tracking-wider">{sub.status || 'Active'}</p>
                    </div>
                    {isActive && <Check size={14} className="text-primary flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccountSwitcher;

AccountSwitcher.propTypes = {
  onBack: PropTypes.func,
  onComplete: PropTypes.func,
};

